import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook de emails pendentes — v3 (Sessão 4.6).
 *
 * Mudanças em relação a v2:
 * - Busca to_emails e cc_emails explicitamente (UI precisa exibir)
 * - Novo: aplicarVinculosCompletos (empresa + empresa_proprietaria + area + oferta + contato + tipo em 1 call)
 * - Novo: refreshEmail (refetch de 1 email específico após ação)
 * - Mantém: classificação IA em lote, marcarComoIgnorado, marcarComoLixo
 *
 * @param {number} limit — teto de linhas lidas em fetchPendentes (default 100,
 *   preservando o modal "Inbox de Aprendizado"). A página de triage (Inbox 2.0)
 *   passa um teto alto para ler a fila inteira sem o cap silencioso de 100.
 *
 * As mutações aceitam um `opts.skipRefetch` opcional: quando true, NÃO refazem o
 * fetch (a página de triage faz UM único refresh após o lote inteiro). Default
 * false → comportamento legado intacto (cada ação refaz o fetch).
 */
export const useEmailsPendentes = (limit = 100) => {
  const [emailsPendentes, setEmailsPendentes] = useState([]);
  const [ofertasPorArea, setOfertasPorArea] = useState(new Map());
  const [stats, setStats] = useState({ 
    total: 0, 
    com_area_detectada: 0,
    sem_sinal: 0,
    por_remetente_tipo: {}
  });
  const [loading, setLoading] = useState(true);
  const [classificando, setClassificando] = useState(false);
  const [ultimoResultado, setUltimoResultado] = useState(null);
  const [error, setError] = useState(null);
  // Mapa canônico domínio→empresa (carregado uma vez). Fonte para resolver a
  // contraparte na ação de lote "Vincular empresa". SPEC §3.
  const [dominiosEmpresa, setDominiosEmpresa] = useState([]);

  const fetchPendentes = useCallback(async () => {
    setLoading(true);
    try {
      const { data: pendentes, error: err } = await supabase
        .from('emails_gmail')
        .select(`
          id, from_email, to_emails, cc_emails, subject, body_text, data_envio, direcao,
          classificacao_status, classificacao_confianca, classificado_por,
          remetente_tipo, area_mencionada_id, empresa_id, empresa_proprietaria_id,
          oferta_id, contato_id, tipo_conteudo, acao_humana,
          empresas:empresa_id (id, empresa),
          empresa_proprietaria:empresa_proprietaria_id (id, empresa),
          areas:area_mencionada_id (id, nome)
        `)
        .eq('acao_humana', 'pendente')
        .order('data_envio', { ascending: false })
        .limit(limit);

      if (err) throw err;
      setEmailsPendentes(pendentes || []);

      const { data: ofertas } = await supabase
        .from('ofertas')
        .select('id, nome, empresa_id, area_id');

      const mapa = new Map();
      (ofertas || []).forEach(o => {
        if (!o.area_id) return;
        if (!mapa.has(o.area_id)) mapa.set(o.area_id, []);
        mapa.get(o.area_id).push(o);
      });
      setOfertasPorArea(mapa);

      const { count: total } = await supabase
        .from('emails_gmail')
        .select('*', { count: 'exact', head: true })
        .eq('acao_humana', 'pendente');

      const { count: comArea } = await supabase
        .from('emails_gmail')
        .select('*', { count: 'exact', head: true })
        .eq('acao_humana', 'pendente')
        .not('area_mencionada_id', 'is', null);

      const porRemetente = {};
      for (const tipo of ['humano_interno', 'humano_externo', 'advogado', 'ata_reuniao', 'ferramenta_automatica']) {
        const { count } = await supabase
          .from('emails_gmail')
          .select('*', { count: 'exact', head: true })
          .eq('acao_humana', 'pendente')
          .eq('remetente_tipo', tipo);
        if (count > 0) porRemetente[tipo] = count;
      }

      setStats({
        total: total || 0,
        com_area_detectada: comArea || 0,
        sem_sinal: (total || 0) - (comArea || 0),
        por_remetente_tipo: porRemetente,
      });

      setError(null);
    } catch (err) {
      console.error('Erro ao buscar emails pendentes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetchPendentes(); }, [fetchPendentes]);

  // Carrega o mapa domínio→empresa uma única vez (tabela canônica pequena).
  // Usado pela ação de lote "Vincular empresa" para resolver a contraparte
  // (lado não-Aston) por domínio. SPEC §3.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('dominios_empresa')
        .select('dominio, empresa_id, empresas:empresa_id (id, empresa)');
      if (cancelado) return;
      if (err) {
        console.warn('Erro ao carregar dominios_empresa:', err.message);
        return;
      }
      setDominiosEmpresa(data || []);
    })();
    return () => { cancelado = true; };
  }, []);

  /**
   * Classifica lote de emails pendentes usando o novo agent-classify-email
   * (contexto rico + regras aprendidas do contexto_negocio + propagacao por
   * thread). Mantido retrocompativel: retorna shape esperado pela UI atual.
   *
   * Pacote classify_email_context_rico_thread 2026-04-26.
   */
  const classificarLote = async (limit = 20) => {
    setClassificando(true);
    setError(null);
    setUltimoResultado(null);
    try {
      const { data, error: err } = await supabase.functions.invoke('agent-classify-email', {
        body: {
          batch_limit: limit,
          mode: 'classify',
          propagate_thread: true,
        },
      });
      if (err) throw err;
      if (!data?.ok) throw new Error(data?.error || 'agent-classify-email falhou');
      setUltimoResultado({
        total: data.total,
        classified: data.classified,
        skipped: data.skipped,
        propagated: data.propagated,
        strategy_breakdown: data.strategy_breakdown,
        fn_version: data.fn_version,
        duration_ms: data.duration_ms,
      });
      await fetchPendentes();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setClassificando(false);
    }
  };

  /**
   * Aplica vínculos múltiplos ao email em 1 chamada (HITL multi-entidade).
   *
   * @param {string} emailId
   * @param {object} vinculos — { empresa_id?, empresa_proprietaria_id?, area_mencionada_id?, oferta_id?, tipo_conteudo?, contato_representa_empresa_id? }
   * @param {string} userEmail
   *
   * Se vinculos.oferta_id for fornecido: marca acao_humana='resolvido' + classificacao humana.
   * Se for NULL: mantém pendente mas atualiza outros vínculos.
   *
   * Adicionalmente, se passar contato_representa_empresa_id, atualiza contatos.representa_empresa_id 
   * para o contato do from_email (aprender "advogado X representa empresa Y" em 1 clique).
   */
  const aplicarVinculosCompletos = async (emailId, vinculos, userEmail, opts = {}) => {
    // Busca estado atual para trilha HITL
    const { data: atual } = await supabase
      .from('emails_gmail')
      .select('oferta_id, contato_id, from_email')
      .eq('id', emailId)
      .single();

    const patch = {};
    if (vinculos.empresa_id !== undefined) patch.empresa_id = vinculos.empresa_id;
    if (vinculos.empresa_proprietaria_id !== undefined) patch.empresa_proprietaria_id = vinculos.empresa_proprietaria_id;
    if (vinculos.area_mencionada_id !== undefined) patch.area_mencionada_id = vinculos.area_mencionada_id;
    // 06/mai/26: vínculo opcional com imóvel específico (emails_gmail.imovel_id)
    if (vinculos.imovel_id !== undefined) patch.imovel_id = vinculos.imovel_id;
    if (vinculos.tipo_conteudo !== undefined) patch.tipo_conteudo = vinculos.tipo_conteudo;

    // Se oferta sendo atribuída: trilha completa
    if (vinculos.oferta_id !== undefined) {
      patch.oferta_id = vinculos.oferta_id;
      patch.oferta_id_anterior = atual?.oferta_id || null;
      patch.classificacao_status = vinculos.oferta_id ? 'humano_classificado' : 'pendente';
      patch.classificacao_confianca = vinculos.oferta_id ? 1.0 : null;
      patch.classificado_por = `humano:${userEmail || 'desconhecido'}`;
      patch.classificado_em = new Date().toISOString();
    }

    // Se marcando como resolvido (teve oferta ou explícito resolver_apos)
    const resolver = vinculos.resolver === true || vinculos.oferta_id;
    if (resolver) {
      patch.acao_humana = 'resolvido';
      patch.acao_humana_em = new Date().toISOString();
      patch.acao_humana_por = userEmail || 'desconhecido';
    }

    const { error: err } = await supabase
      .from('emails_gmail')
      .update(patch)
      .eq('id', emailId);

    if (err) throw err;

    // Se usuário marcou "advogado representa empresa X", atualiza o contato
    if (vinculos.contato_representa_empresa_id !== undefined && atual?.contato_id) {
      const { error: errCont } = await supabase
        .from('contatos')
        .update({ representa_empresa_id: vinculos.contato_representa_empresa_id })
        .eq('id', atual.contato_id);
      if (errCont) console.warn('Erro ao atualizar representa_empresa_id do contato:', errCont);
    }

    if (!opts.skipRefetch) await fetchPendentes();
  };

  const marcarComoIgnorado = async (emailId, userEmail, opts = {}) => {
    const { error: err } = await supabase
      .from('emails_gmail')
      .update({
        acao_humana: 'ignorado',
        acao_humana_em: new Date().toISOString(),
        acao_humana_por: userEmail || 'desconhecido',
      })
      .eq('id', emailId);
    if (err) throw err;
    if (!opts.skipRefetch) await fetchPendentes();
  };

  const marcarComoLixo = async (emailId, userEmail, opts = {}) => {
    const { error: err } = await supabase
      .from('emails_gmail')
      .update({
        acao_humana: 'lixo',
        acao_humana_em: new Date().toISOString(),
        acao_humana_por: `humano:${userEmail || 'desconhecido'}`,
      })
      .eq('id', emailId);
    if (err) throw err;
    if (!opts.skipRefetch) await fetchPendentes();
  };

  return {
    emailsPendentes,
    ofertasPorArea,
    stats,
    loading,
    classificando,
    ultimoResultado,
    error,
    refetch: fetchPendentes,
    classificarLote,
    aplicarVinculosCompletos,
    marcarComoIgnorado,
    marcarComoLixo,
    dominiosEmpresa,
  };
};
